import React, { useState } from "react";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export const MemberBreakdown = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");

  // Main user categories data
  const overviewData = [
    { name: "Caregivers", value: 1450, color: "#8B5CF6" },
    { name: "Care Recipients", value: 1820, color: "#10B981" },
    { name: "Family Members", value: 650, color: "#F59E0B" },
    { name: "Volunteers", value: 320, color: "#3B82F6" }
  ];

  // Professional caregivers by specialty
  const caregiverSpecialtiesData = [
    { name: "Geriatric Care", value: 580, color: "#8B5CF6" },
    { name: "Physical Therapy", value: 340, color: "#EC4899" },
    { name: "Mental Health", value: 210, color: "#6366F1" },
    { name: "Home Health", value: 190, color: "#14B8A6" },
    { name: "Palliative Care", value: 130, color: "#F43F5E" }
  ];

  // Volunteer types
  const volunteerTypesData = [
    { name: "Companion Care", value: 170, color: "#3B82F6" },
    { name: "Transportation", value: 70, color: "#0EA5E9" },
    { name: "Meal Delivery", value: 50, color: "#06B6D4" },
    { name: "Admin Support", value: 30, color: "#0D9488" }
  ];

  const getActiveData = () => {
    switch (activeTab) {
      case "caregivers": return caregiverSpecialtiesData;
      case "volunteers": return volunteerTypesData;
      default: return overviewData;
    }
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  
    return (
      <g>
        <text x={cx} y={cy} dy={-20} textAnchor="middle" fill="#888888" className="text-sm">
          {payload.name}
        </text>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill="#000000" className="text-xl font-semibold">
          {value}
        </text>
        <text x={cx} y={cy} dy={25} textAnchor="middle" fill="#888888" className="text-xs">
          {`${(percent * 100).toFixed(0)}%`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 5}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  // Custom tooltip formatter as a standalone function
  const formatTooltip = (value, name) => [`${value} users`, name];

  return (
    <>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 mb-4">
          <TabsTrigger value="overview">All Members</TabsTrigger>
          <TabsTrigger value="caregivers">Caregivers</TabsTrigger>
          <TabsTrigger value="volunteers">Volunteers</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="h-[250px]">
          <ChartContainer
            config={{
              caregivers: { color: "#8B5CF6" },
              recipients: { color: "#10B981" },
              family: { color: "#F59E0B" },
              volunteers: { color: "#3B82F6" }
            }}
          >
            <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={overviewData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                onMouseEnter={onPieEnter}
              >
                {overviewData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <ChartTooltip
                content={<ChartTooltipContent />}
              />
            </PieChart>
          </ChartContainer>
          <div className="flex justify-center mt-2">
            <ChartLegend>
              <ChartLegendContent payload={
                overviewData.map(item => ({
                  value: item.name,
                  color: item.color,
                  dataKey: item.name.toLowerCase()
                }))
              }/>
            </ChartLegend>
          </div>
        </TabsContent>
        
        <TabsContent value="caregivers" className="h-[250px]">
          <ChartContainer
            config={{
              geriatric: { color: "#8B5CF6" },
              physical: { color: "#EC4899" },
              mental: { color: "#6366F1" },
              home: { color: "#14B8A6" },
              palliative: { color: "#F43F5E" }
            }}
          >
            <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={caregiverSpecialtiesData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                onMouseEnter={onPieEnter}
              >
                {caregiverSpecialtiesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <ChartTooltip
                content={<ChartTooltipContent />}
              />
            </PieChart>
          </ChartContainer>
          <div className="flex justify-center mt-2">
            <ChartLegend>
              <ChartLegendContent payload={
                caregiverSpecialtiesData.map(item => ({
                  value: item.name,
                  color: item.color,
                  dataKey: item.name.toLowerCase().replace(' ', '')
                }))
              }/>
            </ChartLegend>
          </div>
        </TabsContent>
        
        <TabsContent value="volunteers" className="h-[250px]">
          <ChartContainer
            config={{
              companion: { color: "#3B82F6" },
              transportation: { color: "#0EA5E9" },
              meal: { color: "#06B6D4" },
              admin: { color: "#0D9488" }
            }}
          >
            <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={volunteerTypesData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                onMouseEnter={onPieEnter}
              >
                {volunteerTypesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <ChartTooltip
                content={<ChartTooltipContent />}
              />
            </PieChart>
          </ChartContainer>
          <div className="flex justify-center mt-2">
            <ChartLegend>
              <ChartLegendContent payload={
                volunteerTypesData.map(item => ({
                  value: item.name,
                  color: item.color,
                  dataKey: item.name.toLowerCase().replace(' ', '')
                }))
              }/>
            </ChartLegend>
          </div>
        </TabsContent>
      </Tabs>
    </>
  );
};
